import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { cancelCustomerOrder } from '../../api/apiStorefront';
import parseError from '../../utils/parseError';

/**
 * Customer-side order cancel — POST /me/orders/{id}/cancel.
 *
 * Backend rule: only `pending` orders are cancellable. The button
 * MUST already be hidden by the caller for any other status; this
 * hook only handles the request + cache invalidation + the
 * "refund pending" tail copy.
 *
 * Usage:
 *   const cancel = useCancelCustomerOrder();
 *   cancel.mutate({ id, reason });
 */
export const useCancelCustomerOrder = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, reason }) => cancelCustomerOrder(id, reason ? { reason } : {}),
    onSuccess: (response) => {
      const refundPending = response?.data?.data?.refund_pending;
      const base = t(
        'orders.cancelSuccess',
        'Order cancelled. We sent a confirmation to your email.'
      );
      const tail = refundPending
        ? ' ' + t(
            'orders.cancelRefundPending',
            'Your refund will be processed within 5 business days.'
          )
        : '';
      toast.success(base + tail);
      queryClient.invalidateQueries({ queryKey: ['customer-orders'] });
      queryClient.invalidateQueries({ queryKey: ['customer-order'] });
    },
    onError: (err) => {
      toast.error(parseError(err) || t('orders.cancelError', 'Could not cancel the order'));
    },
  });
};

export default useCancelCustomerOrder;
